import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
  AmbasadorIcon,
  BeepIcon,
  CalendarWithStarIcon,
  ChatIcon,
  CupIcon,
  DonorIcon,
  FlagIcon,
  GeoLocationIcon,
  RoadIcon,
  StarIcon,
  UsersIcon2,
  VerifiedIcon,
} from 'public/assets/icons';
import { routesPath } from 'utils/constants/routePath';
import EmptyUserProfilePhoto from 'components/EmptyUserProfilePhoto/EmptyUserProfilePhoto';
import { useTheme } from 'styled-components';
import { InvolvedUserTypes } from 'utils/constants';
import {
  openSidebarChatConversation,
  openSidebarInvolvedUsers,
} from 'store/sideBarControler/actions';

import Button from 'components/common/Button';
import { anonymousClickHandler } from 'utils/anonymousClickHandler';
import { getRoomInfoStart } from 'store/chat/actions';
import SocialActivityAction from 'components/SocialActivityAction';
import imageResize from 'utils/imageResize';
import { IEventDetails } from './IEventDetails';

import {
  BlockGeolocation,
  CanceledEventText,
  ClickableArea,
  EventAttendingItem,
  EventAttendingText,
  EventAttendingWrap,
  EventButtons,
  EventCreatorImageWrap,
  EventCreatorName,
  EventCreatorWrap,
  EventDetailsBlock,
  EventNameText,
  EventPeriod,
  LabelName,
  LabelWrap,
  MessageButtonWrap,
  UserIconWrap,
} from './EventDetails.styles';
import useAttendEvent from './useAttendEvent';
import { AttenderStatus } from 'types';
import { addClubInfoToUrl } from 'utils/helpers';


const EventDetails = ({
  isClickable,
  type,
  isEventHomeTab,
  event,
  data,
  variant = 'default',
  isUpcomingEvent = false,
}: IEventDetails) => {
  const {t} = useTranslation()
  const EVENT_TYPE = [
      {
        id: 0,
        name: t('landing:eventTypes:meetups'),
        value: process.env._EVENT_MEETUPS,
        pinType: 'meetups',
        icon: (color) => <StarIcon color={color} />,
      },
      {
        id: 1,
        name: t('landing:eventTypes:drives'),
        value: process.env._EVENT_DRIVES,
        pinType: 'drives',
        icon: (color) => <RoadIcon color={color} />,
      },
      {
        id: 2,
        name: t('landing:eventTypes:races'),
        value: process.env._EVENT_RACES,
        pinType: 'races',
        icon: (color) => <CupIcon color={color} />,
      },
      {
        id: 3,
        name: t('landing:eventTypes:trackdays'),
        value: process.env._EVENT_TRACK_DAYS,
        pinType: 'trackdays',
        icon: (color) => <FlagIcon color={color} />,
      },
      {
        id: 4,
        name: t('landing:eventTypes:carshows'),
        value: process.env._EVENT_CARS_SHOWS,
        pinType: 'carshows',
        icon: (color) => <BeepIcon color={color} />,
      },
      {
        id: 5,
        name: t('landing:eventTypes:other'),
        value: process.env._EVENT_OTHERS,
        pinType: 'otros',
        icon: (color) => <CalendarWithStarIcon color={color} />,
      },
    ];

  const activeClub = useSelector((state) => state?.club?.activeClub);
  const [currentAttendanceStatus, setCurrentAttendanceStatus] =
    useState<AttenderStatus>(event?.attendingStatus?.status as AttenderStatus);

  const { isAttender, setIsAttender, setShowCancelAttendEvent } =
    useAttendEvent({
      isAttenderData: event?.isAttender,
      attendersCountData: 0,
      attendingStatus: currentAttendanceStatus,
    });

  const router = useRouter();
  const theme: any = useTheme();
  const dispatch = useDispatch();

  const { attendingEvents } = useSelector((state) => state.posts);

  useEffect(() => {
    const { canceled, attended } = attendingEvents;
    const postId = data?.originId || data?._id;

    if (attended.some((el) => el === postId)) {
      setIsAttender(true);
    }

    if (canceled.some((el) => el === postId)) {
      setShowCancelAttendEvent(false);
      setIsAttender(false);
    }
  }, [attendingEvents]);

  const getPostUrl = () => {
    const { origin: URLOrigin } = window.location;
    let url = URLOrigin;

    switch (type) {
      case 1:
      case 3:
        url = `${url}/p/${data?._id}`;
        url = addClubInfoToUrl(url, activeClub);
        break;
      case 4:
        url = `${url}/v/${data?.originId}`;
        url = addClubInfoToUrl(url, activeClub);
        break;
      case 16:
      case 6:
        url = `${url}/e/${data?.originId}`;
        url = addClubInfoToUrl(url, activeClub);
        break;
      case 7:
        url = `${url}/g/${data?.originId}`;
        url = addClubInfoToUrl(url, activeClub);
        break;
      default:
        break;
    }

    return url;
  };

  const onPostClick = () => {
    if (isClickable) {
      router.push(getPostUrl());
    }
  };

  const renderEventCreator = () => {
    const eventCreator = event?.creator || data.creator?.data;

    if (!eventCreator) {
      return null;
    }

    return (
      <EventCreatorWrap
        onClick={() =>
          router.push(`${routesPath.user_details}/${eventCreator?._id}`)
        }
        isEventHomeTab={isEventHomeTab}
      >
        {eventCreator?.profilePhoto && eventCreator?.profilePhoto?.trim() ? (
          <EventCreatorImageWrap
            img={
              imageResize(eventCreator?.profilePhoto, '_thumb') ||
              eventCreator?.profilePhoto
            }
            role="img"
            aria-label="event creator img"
          />
        ) : (
          <EmptyUserProfilePhoto
            wrapSize={24}
            userIconSize={12}
            withBorder={false}
            margin="0 8px 0 0"
          />
        )}

        <EventCreatorName>{t('event:eventBy')}</EventCreatorName>
        <EventCreatorName color={theme.colors.black} ml={4}>
          {eventCreator?.username}
        </EventCreatorName>

        {eventCreator?.isVerified && (
          <UserIconWrap>
            <VerifiedIcon color={theme.colors.neutral.n8} />
          </UserIconWrap>
        )}

        {eventCreator?.isAmbassador && (
          <UserIconWrap>
            <AmbasadorIcon color={theme.colors.neutral.n8} />
          </UserIconWrap>
        )}

        {eventCreator?.isDonor && (
          <UserIconWrap>
            <DonorIcon color={theme.colors.neutral.n8} />
          </UserIconWrap>
        )}
      </EventCreatorWrap>
    );
  };

  const renderEventAttending = () => {
    if (variant !== 'event_details') {
      return null;
    }

    const isAttendingClickable = !!data?.attendersCount;
    const isInvitedClickable = !!data?.invitedCount;

    const onClick = (involvedUserType, isElementClickable) => {
      if (!isElementClickable) {
        return;
      }

      dispatch(
        openSidebarInvolvedUsers({
          id: data?._id,
          isAdmin: data?.isAdmin,
          involvedUserType,
        })
      );
    };

    return (
      <EventAttendingWrap>
        <UsersIcon2 />

        <EventAttendingItem
          ml={9}
          mr={9}
          isClickable={isAttendingClickable}
          onClick={() =>
            onClick(InvolvedUserTypes.EVENT_ATTENDING, isAttendingClickable)
          }
        >
          <EventAttendingText color={theme.colors.black}>
            {data?.attendersCount}
          </EventAttendingText>
          <EventAttendingText ml={4}>{t('event:Attending')}</EventAttendingText>
        </EventAttendingItem>
        {!isUpcomingEvent && (
          <EventAttendingItem
            isClickable={isInvitedClickable}
            onClick={() =>
              onClick(InvolvedUserTypes.EVENT_INVITED, isInvitedClickable)
            }
          >
            <EventAttendingText color={theme.colors.black}>
              {data?.invitedCount}
            </EventAttendingText>
            <EventAttendingText ml={4}>{t('event:Invited')}</EventAttendingText>
          </EventAttendingItem>
        )}
      </EventAttendingWrap>
    );
  };

  const openLocation = (data: any) => {
    const longitude = data?.lng;
    const latitude = data?.lat;
    const locationName = data?.address;
    if (!latitude || !longitude || !locationName) {
      return;
    }
    router.push({
      pathname: routesPath.mapPath,
      query: {
        lat: parseFloat(latitude),
        lng: parseFloat(longitude),
        locationName,
      },
    });
  };

  return (
    <>
      <EventDetailsBlock>
        <ClickableArea isClickable={isClickable} onClick={onPostClick}>
          <EventPeriod>
            {/* <CupIcon width="16" height="16" /> */}
            {event?.pinType && (
              <>
                <LabelWrap>
                  {EVENT_TYPE.find((item) => item.pinType === event?.pinType)?.icon(
                    '#A9A9B5'
                  )}
                  <LabelName>
                    {' '}
                    {
                      EVENT_TYPE.find((item) => item.pinType === event?.pinType)
                        ?.name
                    }
                  </LabelName>
                </LabelWrap>
              </>
            )}
            <p>
              {moment(event?.startDate || data?.startDate).format(
                'DD MMM hh:mmA'
              )}
            </p>
            <p>{' - '}</p>
            <p>
              {moment(event?.endDate || data?.endDate).format('DD MMM hh:mmA')}
            </p>
          </EventPeriod>
          <EventNameText>{event?.name || data?.name}</EventNameText>
          {type !== 7 && (
            <>
              {(event?.location || data?.address) && (
                <BlockGeolocation
                  fs={13}
                  mb={16}
                  ml={8}
                  onClick={() => openLocation(event?.location || data)}
                >
                  <GeoLocationIcon />
                  <p>{event?.location?.address || data?.address}</p>
                </BlockGeolocation>
              )}
            </>
          )}
        </ClickableArea>
        {type === 16 && renderEventCreator()}
        {renderEventAttending()}
        {!isEventHomeTab && (
          <EventButtons>
            {(event?.isCanceled || data?.isCanceled) &&
            variant === 'event_details' ? (
              <CanceledEventText>
                {t('event:eventIsCanceled')}
              </CanceledEventText>
            ) : (
              <>
                {/* This button opens the event page. Commented out due to client requirements. */}
                {/* {!(event?.isCanceled || data?.isCanceled || event?.isAdmin) && (
                  <Button
                    variant="primary"
                    onClick={() => {
                      anonymousClickHandler(() => {
                        router.push(getPostUrl());
                      });
                    }}
                    width="full"
                  >
                    {t('event:openEvent')}
                  </Button>
                )} */}
                <Button
                  variant="default"
                  onClick={() => {
                    if (variant === 'event_details') {
                      anonymousClickHandler(() => null);
                    } else {
                      anonymousClickHandler(() =>
                        router.push(
                          `${routesPath.event_details}/${data?.originId}`
                        )
                      );
                    }
                  }}
                  width="full"
                >
                  {t('event:inviteUppercase') || type === 6
                    ? t('common:moreInfo')
                    : t('event:eventDetailsUppercase')}
                </Button>
                {variant === 'event_details' &&
                  data?.isChat &&
                  (data?.isAdmin || isAttender) && (
                    <MessageButtonWrap>
                      <Button
                        buttonType="icon"
                        onClick={() =>
                          anonymousClickHandler(() => {
                            dispatch(getRoomInfoStart(data?._id));
                            dispatch(
                              openSidebarChatConversation({
                                chatId: data?._id,
                              })
                            );
                          })
                        }
                      >
                        <ChatIcon />
                      </Button>
                    </MessageButtonWrap>
                  )}
              </>
            )}
          </EventButtons>
        )}
        {variant === 'event_details' && !isUpcomingEvent && (
          <SocialActivityAction
            isLike={data?.isLike}
            likes={data?.likesCount}
            isEventLikes
            commentsCount={data?.commentsCount}
            sharesCount={data?.sharesCount}
            id={data?._id}
            originId={data?.originId}
          />
        )}
      </EventDetailsBlock>
    </>
  );
};

export default EventDetails;
